import { Action } from 'redux';
import { AppState, history } from 'src';
import { AppDispatcher } from '@models/common.models';
import { contractService } from '@services/ContractService';
import { requestAction } from '@common/components/common.actions';
import { ContractViewModel } from '@models/Contract';
import { CommentType, CommentViewModel } from '@models/Comment';
import { saveComment } from 'src/comments/comments.actions';
import { RouteDefinitions, getRoute } from '@common/route-definitions';

export enum OrderEditActionType {
  SetOrderEditLoading = 'SetOrderEditLoading',
  SetIsStoringPropertyInfo = 'SetIsStoringPropertyInfo',
  SetIsStoringAffiliatesInfo = 'SetIsStoringAffiliatesInfo',
  SetIsStoringPriceAdjustmentInfo = 'SetIsStoringPriceAdjustmentInfo',
  SetIsStoringCoverageInfo = 'SetIsStoringCoverageInfo'
}

export interface SetOrderEditLoadingAction extends Action {
  readonly type: OrderEditActionType.SetOrderEditLoading;
  readonly isLoading: boolean;
}

export interface SetIsStoringAction extends Action {
  readonly type: OrderEditActionType;
  readonly isStoring: boolean;
}

export function setOrderEditLoading(loading: boolean) {
  return {
    type: OrderEditActionType.SetOrderEditLoading,
    isLoading: loading
  };
}

export function setOrderEditStoring(type: OrderEditActionType): (isStoring: boolean) => SetIsStoringAction {
  return (isStoring: boolean) => {
    return {
      type: type,
      isStoring: isStoring
    };
  };
}

export const setIsStoringPropertyInfo = setOrderEditStoring(OrderEditActionType.SetIsStoringPropertyInfo);
export const setIsStoringAffiliatesInfo = setOrderEditStoring(OrderEditActionType.SetIsStoringAffiliatesInfo);
export const setIsStoringPriceAdjustmentInfo = setOrderEditStoring(OrderEditActionType.SetIsStoringPriceAdjustmentInfo);
export const setIsStoringCoverageInfo = setOrderEditStoring(OrderEditActionType.SetIsStoringCoverageInfo);

export function saveOrderEdit(contractId: number, callback?: Function) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    const state = getState();
    var isStoring =
      // state.contract.orderEdit.isStoringAffiliatesInfo ||
      // state.contract.orderEdit.isStoringCoverageInfo ||
      // state.contract.orderEdit.isStoringPriceAdjustmentInfo ||
      state.contract.orderEdit.isStoringPropertyInfo;
    if (isStoring) {
      setTimeout(() => dispatch(saveOrderEdit(contractId, callback)), 500);
      return;
    }
    const stateContractVM = state.contract.contractVM;
    if (stateContractVM) {
      let contractVM = { ...stateContractVM } as ContractViewModel;

      if (stateContractVM.transactionCoordinatorIds && stateContractVM.transactionCoordinatorIds.length > 0) {
        contractVM.transCoordId =
          stateContractVM.transactionCoordinatorIds[0] && stateContractVM.transactionCoordinatorIds[0].id;
        var transCoords = stateContractVM.transactionCoordinatorIds.slice(1);
        transCoords.forEach(tc => {
          contractVM.contractEntities.push({
            id: 0,
            contractId: 0,
            transactionCoordinatorId: tc.id
          });
        });
      }
      if (stateContractVM.additionalRealEstateAgentIds && stateContractVM.additionalRealEstateAgentIds.length > 0) {
        stateContractVM.additionalRealEstateAgentIds.forEach(ra => {
          contractVM.contractEntities.push({
            id: 0,
            contractId: 0,
            realEstateAgentId: ra
          });
        });
      }
      if (stateContractVM.totalPrice < 0) {
        contractVM.totalPrice = 0;
      }
      updateContract(state, contractVM);
      await dispatch(
        requestAction(
          () =>
            contractService.updateContract(contractId, contractVM).then(() => {
              callback!();
              history.push(getRoute(RouteDefinitions.contract.PaymentEdit, { id: +contractId }));
            }),
          () => {
            return { type: '' };
          },
          { hasToast: true, toastSuccessMessage: 'Contract Order Updated Successfully.' }
        )
      );
      const commentForm = state.forms.commentForm;
      if (commentForm) {
        const commentField = commentForm.comment;
        const callerNameField = commentForm.callerName;
        if (commentField && callerNameField) {
          let commentValue = commentField.value;
          const callerNameValue = callerNameField.value;
          if (commentValue && callerNameValue) {
            commentValue = 'Caller: ' + callerNameValue + '. ' + commentValue;
            const usersToAction = state.comment.usersToAction;
            return await dispatch(
              saveComment(
                contractId,
                CommentType.Contract,
                { comment: commentValue } as CommentViewModel,
                usersToAction ? usersToAction : []
              )
            );
          }
        }
      }
    }
  };
}

function updateContract(state: AppState, contractVM: ContractViewModel): void {
  if (contractVM) {
    if (state.propertyInfoForm.contactName) {
      contractVM.contactName = state.propertyInfoForm.contactName;
    }
    if (state.propertyInfoForm.contactType) {
      contractVM.contactType = state.propertyInfoForm.contactType;
    }
    if (state.propertyInfoForm.contactMedium) {
      contractVM.contactMedium = state.propertyInfoForm.contactMedium;
    }
    if (state.propertyInfoForm.contactInit) {
      contractVM.contactInit = state.propertyInfoForm.contactInit;
    }
    if (state.propertyInfoForm.propertyAddress) {
      contractVM.propertyAddress = state.propertyInfoForm.propertyAddress;
    }
    if (state.propertyInfoForm.propertyType) {
      contractVM.propertyType = state.propertyInfoForm.propertyType;
    }
    if (state.propertyInfoForm.yearBuilt) {
      contractVM.yearBuilt = state.propertyInfoForm.yearBuilt;
    }

    if (state.contract.initAgent) {
      contractVM.initAgentId = state.contract.initAgent.id;
      contractVM.initOfficeId = state.contract.initOffice ? state.contract.initOffice.id : 0;
    } else {
      contractVM.initAgentId = undefined;
      contractVM.initOfficeId = 0;
    }

    if (state.contract.coopAgent) {
      contractVM.coopAgentId = state.contract.coopAgent.id ? state.contract.coopAgent.id : 0;
      contractVM.coopOfficeId = state.contract.coopOffice ? state.contract.coopOffice.id : 0;
    } else {
      contractVM.coopAgentId = 0;
      contractVM.coopOfficeId = 0;
    }

    if (state.contract.escrowOfficer && state.contract.escrowOfficer.id) {
      contractVM.escrowOfficerId = state.contract.escrowOfficer.id;
      contractVM.escrowId = state.contract.escrowOffice ? state.contract.escrowOffice.id : 0;
    } else {
      contractVM.escrowOfficerId = 0;
      contractVM.escrowId = 0;
    }

    if (state.contract.transCoordId) {
      contractVM.transCoordId = state.contract.transCoordId;
    } else {
      contractVM.transCoordId = undefined;
    }
    if (state.contract.propertyManager) {
      contractVM.propertyManagerId = state.contract.propertyManager.id;
    } else {
      contractVM.propertyManagerId = 0;
    }
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/order-edit/order-edit.actions.ts